/**
 * Themed counterparty combobox. Replaces the native <datalist> so we can style
 * it end-to-end and get keyboard nav that matches the ledger vibe.
 *
 * Props:
 *   value: string (raw text the user has typed)
 *   onChange: (str) => void
 *   options: [{id, name, balance, active}]   (already filtered to visible active)
 *   onSelect: (option) => void               (fires when user picks a row)
 *   onSubmit: () => void                     (fires when the user hits Enter WITHOUT any dropdown match — lets caller commit)
 *   placeholder, testId, inputRef
 */
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { num } from "../lib/api";

const CounterpartyCombobox = forwardRef(function CounterpartyCombobox(
  { value, onChange, options, onSelect, onSubmit, placeholder = "Counterparty…", testId, className = "", inputClassName },
  ref,
) {
  const inputRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    select: () => inputRef.current?.select(),
  }), []);

  const filtered = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return options.slice(0, 30);
    // Rank: name starts-with > substring, keep top 30.
    const starts = [];
    const contains = [];
    for (const o of options) {
      const n = o.name.toLowerCase();
      if (n === q) starts.unshift(o);
      else if (n.startsWith(q)) starts.push(o);
      else if (n.includes(q)) contains.push(o);
    }
    return [...starts, ...contains].slice(0, 30);
  }, [value, options]);

  useEffect(() => { setActive(0); }, [value, open]);

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (!popRef.current || !inputRef.current) return;
      if (popRef.current.contains(e.target) || inputRef.current.contains(e.target)) return;
      setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", h);
      return () => document.removeEventListener("mousedown", h);
    }
  }, [open]);

  const pick = (o) => {
    onSelect?.(o);
    onChange(o.name);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (open && filtered[active]) {
        e.preventDefault();
        pick(filtered[active]);
      } else {
        // Fall back to caller's submit (e.g. auto-resolve on unique prefix)
        onSubmit?.(e);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          // Close on blur unless the blur was caused by a click inside our own
          // popover (a mouse-pick). The popover items use onMouseDown to run
          // pick() before this blur fires, so by the time we're here the
          // relatedTarget is either an item (still inside popRef) or something
          // truly external. In either case setting a short delay lets a click
          // finish, then closes. This prevents the dropdown from lingering when
          // the user tabs away to the amount / notes / Post button.
          const next = e.relatedTarget;
          if (next && popRef.current?.contains(next)) return;
          setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={onKey}
        data-testid={testId}
        className={inputClassName || "w-full px-2 py-1.5 rounded-lg bg-white border border-stone-200 outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40 text-sm"}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div
          ref={popRef}
          className="absolute z-40 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-stone-200 shadow-xl combobox-pop"
          data-testid={testId ? `${testId}-pop` : undefined}
          style={{ background: "var(--glass-strong)", backdropFilter: "blur(20px) saturate(160%)" }}
        >
          {filtered.map((o, i) => (
            <button
              key={o.id}
              type="button"
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => pick(o)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 text-sm transition-colors ${
                i === active ? "bg-[color:var(--gold-soft)] text-[color:var(--gold-deep)]" : ""
              }`}
              data-testid={testId ? `${testId}-opt-${o.id}` : undefined}
            >
              <span className="font-medium truncate">{o.name}</span>
              <span
                className={`text-xs font-serif tabular-nums flex-shrink-0 ${
                  o.balance > 0
                    ? "text-emerald-700"
                    : o.balance < 0
                      ? "text-rose-700"
                      : "text-stone-500"
                }`}
              >
                {num(o.balance)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default CounterpartyCombobox;
